import cn from "classnames";
import { useEffect, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate, useParams } from "react-router-dom";

// Mui Components
import {
    Typography,
    Box,
    Table,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableBody,
    Alert,
    Collapse,
    IconButton,
    Tooltip,
    CircularProgress,
    Skeleton,
    SelectChangeEvent,
    InputAdornment,
    TextField,
    Checkbox,
    Chip,
    FormLabel,
    ListItemText,
    MenuItem,
    Select,
} from "@mui/material";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";

// Utils
import { format } from "date-fns";
import { useAuth } from "utils/hooks";
import { tableHeaderConfig } from "./config";
import { debounce, isEqual, isEmpty } from "lodash";
import { convertClientHubUser, ConvertedClientHubUser } from "./adapters/converters";
import {
    getPermissionNameFromRolesList,
    getUserRolesByPermissions,
    getUserRolesWithCustomPlaceholder,
    snakeCaseToCapitalizedString,
} from "utils/utils";

// Styles
import clientHubStyles from "./ClientHub.styles";

// Store
import { useAppDispatch, useAppSelector } from "app/hooks";
import { getLoaderState, setToastConfig, toggleLoader } from "store/slice/UI";

// Enums and Constants
import { urlPaths } from "enums/urlPaths";
import { initialPagination } from "constants/constants";

// Components
import icons from "enums/icons";
import Modal from "components/Modal";
import { UserRoleSelect, UsersTableRow } from "./components";
import { SortMenu } from "components/SortMenu";
import CustomIcon from "components/CustomIcon";
import NavButton from "components/HeaderNavigation/NavButton";
import { Body, Header } from "components/Layout/LayoutContent/LayoutContent";
import { Permissions } from "pages/HubManagementNew/components/Permissions";
import { CardMessage } from "components/Message";
import TablePagination from "components/Talent/TablePagination";
import {
    ClientHubByIdResponse,
    clientHubUsersFilters,
    ClientHubUsersResponse,
    fetchClientHubByOrgId,
    fetchClientHubUsersByOrdgId,
    removeUserFromGroup,
    Role,
    RoleBundle,
    RoleBundleResponse,
    TClientHubUsersFiltersPayload,
    updateClientHub,
    updateUserFunction,
    updateUserRoles,
} from "services";

// Types
import { Pagination, SimpleEntity, TUserRole } from "types";
import { RequestInfo } from "store/slice/store.types";
import { OrderTable, OrderTableBy } from "components/SortMenu/SortMenu";
import { TSelectedPermissions } from "pages/HubManagementNew/components/Permissions/Permissions";
import { userFunctions } from "services/user_management/user_management.types";
import { ClearFilterButton } from "components/Buttons";
import { fetchPermissionsRefBundleThunk } from "store/slice/sysadmin/permissions/permissionsSlice";
import { AnyAction } from "redux";
import { AxiosResponse } from "axios";
import { TagName } from "components/TagName";

type Filters = {
    filter_name: string;
    filter_functions: string[];
};

export type ClientHubUsersData = {
    data: ConvertedClientHubUser[];
    meta: Pagination;
    status: RequestInfo;
};

const DEFAULT_PAGE_SIZE = 10;
const FILTERS_INITIAL_VALUE: Filters = { filter_name: "", filter_functions: ["placeholder"] };
const DEFAULT_SORT = { columnId: "name", direction: "asc" as OrderTable };

const ClientHub = () => {
    const intl = useIntl();
    const classes = clientHubStyles();

    const { loggedInUserData } = useAuth();

    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { org_id } = useParams();

    const isFetching = useAppSelector(getLoaderState);

    const [isLoading, setIsLoading] = useState(false);

    const previousFilters = useRef(FILTERS_INITIAL_VALUE);
    const [filterUserInput, setFilterUserInput] = useState("");

    const [clientHub, setClientHub] = useState<ClientHubByIdResponse>({} as ClientHubByIdResponse);
    const [clientHubUsers, setClientHubUsers] = useState<ClientHubUsersData>({
        data: [],
        meta: initialPagination,
        status: "pristine",
    });

    const [currentSort, setCurrentSort] = useState<OrderTableBy>(DEFAULT_SORT);
    const [filters, setFilters] = useState<Filters>(FILTERS_INITIAL_VALUE);
    const [selectedUser, setSelectedUser] = useState<SimpleEntity | undefined>(undefined);
    const [userRoles, setUserRoles] = useState<TUserRole[]>([]);

    const [permissionsBundle, setPermissionsBundle] = useState<RoleBundleResponse>({} as RoleBundleResponse);
    const [selectedPermission, setSelectedPermission] = useState<TSelectedPermissions>({
        ldPermission: {} as RoleBundle,
        pacePermission: {} as RoleBundle,
    });

    const [inputPage, setInputPage] = useState<number | string>(1);
    const previousPageValue = useRef<number | string>(1);
    const paginationTextInputRef = useRef<any>(null);

    const adminsCount = clientHub?.users_count_org_admin;
    const isFiltering = !isEqual(filters, FILTERS_INITIAL_VALUE);

    const handleOpenConfirmationModal = (event: React.MouseEvent<HTMLElement, MouseEvent>, userData: SimpleEntity) => {
        event.stopPropagation();
        setSelectedUser(userData);
    };

    const handleCloseConfirmationModal = () => {
        setSelectedUser(undefined);
    };

    const handleAddSelectedUsers = () => {
        navigate(`${urlPaths.HubManagementAddUsers}/${org_id}`);
    };

    const handleGoToHubManagement = () => {
        navigate(urlPaths.HubManagement);
    };

    const handleSelectPermission = async (permission: RoleBundle, type: keyof TSelectedPermissions) => {
        if (!org_id) return;

        const permissionType = type === "ldPermission" ? "LD Permission" : "PACE Permission";
        const newPermissions = { ...selectedPermission, [type]: permission };
        const newPermissionsRoles = Object.values(newPermissions)
            .flatMap((p) => p?.roles)
            .map((role) => role?.name)
            .filter((role) => role !== null && role !== undefined);

        setSelectedPermission(newPermissions);

        try {
            setIsLoading(true);
            await updateClientHub(Number(org_id), { pay_wall_role_masks: newPermissionsRoles });
            dispatch(
                setToastConfig({
                    title: intl.formatMessage({
                        id: "hubManagement.client.message.updateClientHubPermissionTitle",
                    }),
                    content: intl.formatMessage(
                        { id: "hubManagement.client.message.updateClientHubPermissionContent" },
                        { permissionType, newPermission: permission.label }
                    ),
                })
            );
        } catch (error) {
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    };

    // --------------------------------------------------------------------------------------------------------------
    // Users Functions
    // --------------------------------------------------------------------------------------------------------------
    const handleUpdateUserRole = async (event: SelectChangeEvent, userId: number) => {
        const selectedRole = userRoles.filter((role) => role.id === event.target.value)[0];
        const roles = selectedRole.userRoles as Role[];
        const userData = clientHubUsers.data.filter((user) => user.id === userId)[0];

        setClientHubUsers((prevData) => ({
            ...prevData,
            data: prevData.data.map((user) => ({
                ...user,
                function: userId === user.id ? event.target.value : user.function,
                roles: userId === user.id ? roles : user.roles,
                status: userId === user.id ? "saving" : user.status,
            })),
        }));

        try {
            await updateUserRoles(userId, clientHub.group.id, { roles: roles.map((role) => role.name) });
            await updateUserFunction(userId, clientHub.group.id, { function: event.target.value as userFunctions });
            dispatch(
                setToastConfig({
                    title: intl.formatMessage({ id: "userManagement.changeRole.toast.title" }),
                    content: intl.formatMessage(
                        { id: "userManagement.changeRole.toast.content" },
                        { userName: userData.name, newRole: snakeCaseToCapitalizedString(event.target.value) }
                    ),
                })
            );

            setClientHubUsers((prevData) => ({
                ...prevData,
                data: prevData.data.map((user) => ({
                    ...user,
                    status: userId === user.id ? "done" : user.status,
                })),
            }));
        } catch (error) {
            console.error(error);
            setClientHubUsers((prevData) => ({
                ...prevData,
                data: prevData.data.map((user) => ({
                    ...user,
                    function: userId === user.id ? userData.function : user.function,
                    roles: userId === user.id ? userData.roles : user.roles,
                    status: userId === user.id ? "error" : user.status,
                })),
            }));
        }
    };

    const handleRemoveUser = async (selectedUser: SimpleEntity | undefined) => {
        if (!selectedUser) return;

        const newSelectedUser = selectedUser;
        handleCloseConfirmationModal();
        setClientHubUsers((prevData) => ({
            ...prevData,
            data: prevData.data.map((user) => ({
                ...user,
                status: newSelectedUser.id === user.id ? "removing" : user.status,
            })),
        }));

        try {
            await removeUserFromGroup(newSelectedUser.id, { groups: [clientHub.group.id] });

            const { data: clientHubData } = await fetchClientHubByOrgId(Number(org_id));
            setClientHub(clientHubData);

            setClientHubUsers((prevData) => ({
                ...prevData,
                data: prevData.data.filter((user) => user.id !== newSelectedUser.id),
            }));

            dispatch(
                setToastConfig({
                    title: intl.formatMessage({ id: "hubManagement.client.message.removeUserTitle" }),
                    content: intl.formatMessage(
                        { id: "hubManagement.client.message.removeUserContent" },
                        { userName: newSelectedUser?.name, clientHubName: clientHub.name }
                    ),
                })
            );
        } catch (error) {
            console.error(error);
        }
    };

    const handleFetchUsers = async (params: Partial<TClientHubUsersFiltersPayload>) => {
        if (isEqual(filters, previousFilters.current) && isEmpty(params)) return;

        const isDefaultRole = params?.filter_functions?.some((val) => val === "placeholder") ?? true;

        const requestPayload = {
            ...(params?.filter_name ? { filter_name: params.filter_name } : {}),
            ...(!isDefaultRole ? { filter_functions: params.filter_functions } : {}),
            order_by: params?.order_by ? params.order_by : `${currentSort.columnId},${currentSort.direction}`,
            page_no: params?.page_no ? params.page_no : 1,
            page_size: DEFAULT_PAGE_SIZE,
        };

        try {
            setClientHubUsers((data) => ({ ...data, status: "fetching" }));
            const { data } = await fetchClientHubUsersByOrdgId(Number(org_id), requestPayload);
            setClientHubUsers({
                data: convertClientHubUser(data.results, params.clientHub?.group),
                meta: data.meta,
                status: "done",
            });
            previousFilters.current = isEmpty(params) ? filters : FILTERS_INITIAL_VALUE;
        } catch (error) {
            console.error(error);
            setClientHubUsers((data) => ({ ...data, status: "error" }));
        }
    };

    // --------------------------------------------------------------------------------------------------------------
    // Filters and Sorting Functions
    // --------------------------------------------------------------------------------------------------------------
    const delayedFilterName = useRef(
        debounce((value: string, filters: Filters, clientHub: ClientHubByIdResponse) => {
            handleFetchUsers({ ...filters, filter_name: value, clientHub, page_size: DEFAULT_PAGE_SIZE, page_no: 1 });
        }, 500)
    ).current;
    useEffect(() => () => delayedFilterName.cancel(), [delayedFilterName]);

    const handleChangeFilters = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<string[]>
    ) => {
        setInputPage(1);

        if (event.target.name === "name-filter") {
            const newValue = (event as React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>).target.value;
            setFilterUserInput(newValue);

            const newFilters = { ...filters, filter_name: newValue };
            setFilters(newFilters);
            delayedFilterName(newValue, newFilters, clientHub);
        }

        if (event.target.name === "roles-filter") {
            let newValue = event.target.value as string[];

            // If last selected value all (default), reset selection to default
            if ((newValue.length > 1 && newValue.at(-1) === "placeholder") || !newValue.length) {
                handleClearFilters("filter_functions");
                return;
            }

            newValue = newValue.filter((value) => value !== "placeholder");
            const newFilters = { ...filters, filter_functions: newValue };

            setFilters(newFilters);
            handleFetchUsers({
                ...newFilters,
                order_by: `${currentSort.columnId},${currentSort.direction}`,
                clientHub,
            });
        }
    };

    const handleClearFilters = (filterName: "filter_functions" | "all") => {
        if (filterName === "all") {
            setFilterUserInput("");
            setInputPage(1);
            setFilters(FILTERS_INITIAL_VALUE);
            setCurrentSort(DEFAULT_SORT);
            handleFetchUsers({
                ...FILTERS_INITIAL_VALUE,
                order_by: `${DEFAULT_SORT.columnId},${DEFAULT_SORT.direction}`,
                clientHub,
            });
            return;
        }
        if (filterName) {
            const newFilters = { ...filters, [filterName]: ["placeholder"] };
            setFilters(newFilters);
            handleFetchUsers({
                ...newFilters,
                order_by: `${currentSort.columnId},${currentSort.direction}`,
                clientHub,
            });
        }
    };

    const handleSort = (direction: OrderTable, columnId: string | number) => {
        setCurrentSort({ columnId, direction });
        handleFetchUsers({
            ...filters,
            page_size: clientHubUsers.meta.page_size,
            page_no: typeof inputPage === "string" ? parseInt(inputPage || "1") : inputPage,
            order_by: `${columnId},${direction}`,
            clientHub,
        });
    };

    // --------------------------------------------------------------------------------------------------------------
    // Pagination functions
    // --------------------------------------------------------------------------------------------------------------
    const handlePageChange = async (page: number) => {
        if (page < 1 || page > clientHubUsers.meta.pages) {
            return;
        }
        setInputPage(page);
        handleFetchUsers({
            ...filters,
            order_by: `${currentSort.columnId},${currentSort.direction}`,
            page_size: clientHubUsers.meta.page_size,
            page_no: page,
            clientHub,
        });
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.value === "") {
            setInputPage("");
            return;
        }
        const inputValue = Number(event.target.value);
        if (inputValue > 0 && inputValue <= clientHubUsers.meta.pages) {
            setInputPage(inputValue);
        }
    };
    const handleOnKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter") {
            if (inputPage === clientHubUsers.meta.page_no) {
                return;
            }

            paginationTextInputRef.current.blur();
        }

        if (event.key === "Escape") {
            setInputPage(previousPageValue.current || 1);
            setTimeout(() => {
                paginationTextInputRef.current.blur();
            }, 1);
            return;
        }
    };
    const handleOnBlur = () => {
        if (inputPage === "" || inputPage === clientHubUsers.meta.page_no) {
            setInputPage(clientHubUsers.meta.page_no);
            return;
        }

        if (Boolean(inputPage) && Number(inputPage) > 0 && Number(inputPage) <= clientHubUsers.meta.pages) {
            previousPageValue.current = inputPage;
            handlePageChange(Number(inputPage));
        }
    };

    // --------------------------------------------------------------------------------------------------------------
    // Initial Fetch
    // --------------------------------------------------------------------------------------------------------------
    useEffect(() => {
        const initialFetch = async () => {
            const promises = [];
            dispatch(toggleLoader(true));

            promises.push(dispatch(fetchPermissionsRefBundleThunk()));
            promises.push(fetchClientHubByOrgId(Number(org_id)));
            promises.push(
                fetchClientHubUsersByOrdgId(Number(org_id), { ...clientHubUsersFilters, page_size: DEFAULT_PAGE_SIZE })
            );

            setClientHubUsers((prevValue) => ({ ...prevValue, status: "fetching" }));

            if (promises.length) {
                const data = await Promise.allSettled(promises);
                let clientHub = {} as ClientHubByIdResponse;
                let clientHubUsers = {} as ClientHubUsersResponse;
                let permissionsBundle = {} as RoleBundleResponse;

                for (const result of data) {
                    if (result.status === `rejected`) {
                        dispatch(toggleLoader(false));
                        return;
                    }

                    if (result.status === `fulfilled`) {
                        if (
                            (result.value as AxiosResponse<ClientHubByIdResponse>).config?.url ===
                            `/sys_admin/client_hub/${org_id}`
                        ) {
                            clientHub = (result.value as AxiosResponse<ClientHubByIdResponse>).data;
                            setClientHub(clientHub);
                        }

                        if (
                            (result.value as AxiosResponse<ClientHubUsersResponse>)?.config?.url?.includes(
                                `/sys_admin/client_hub/${org_id}/users`
                            )
                        ) {
                            clientHubUsers = (result.value as AxiosResponse<ClientHubUsersResponse>).data;
                        }

                        if ((result.value as AnyAction)?.type === `sysadmin/permissionsBundleRef/fetch/fulfilled`) {
                            permissionsBundle = (result.value as AnyAction)?.payload as RoleBundleResponse;
                            setPermissionsBundle(permissionsBundle);
                        }
                    }
                }

                if (!isEmpty(clientHub) && !isEmpty(permissionsBundle)) {
                    const ldPermission = getPermissionNameFromRolesList(permissionsBundle, clientHub.roles_ld, "LD");
                    const pacePermission = getPermissionNameFromRolesList(
                        permissionsBundle,
                        clientHub.roles_pace,
                        "PACE"
                    );

                    setSelectedPermission({ ldPermission, pacePermission });
                    setClientHubUsers({
                        data: convertClientHubUser(clientHubUsers.results, clientHub.group),
                        meta: clientHubUsers.meta,
                        status: "done",
                    });
                }
            }

            dispatch(toggleLoader(false));
        };

        if (org_id) {
            initialFetch();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // --------------------------------------------------------------------------------------------------------------
    // Observe changes in selected permissions and create a list of user roles accordingly
    // --------------------------------------------------------------------------------------------------------------
    useEffect(() => {
        if (isEmpty(permissionsBundle)) return;

        const rolesWithUpdatedPlaceholder = getUserRolesWithCustomPlaceholder();
        const userRoles = getUserRolesByPermissions(selectedPermission, permissionsBundle, rolesWithUpdatedPlaceholder);

        setUserRoles(userRoles);
    }, [permissionsBundle, selectedPermission]);

    return (
        <>
            <Header backgroundColor="primary">
                <Box className={classes.headerInner}>
                    <Box>
                        {!isFetching ? (
                            <TagName
                                sx={{
                                    fontSize: 24,
                                    fontWeight: 600,
                                    borderWidth: 2,
                                    borderRadius: 3,
                                    px: 1.5,
                                    mb: 1,
                                }}
                            >
                                {clientHub.name}
                            </TagName>
                        ) : (
                            <Skeleton variant="rectangular" animation="wave" width={200} height={55} sx={{ mb: 0.5 }} />
                        )}
                        {!isFetching ? (
                            <Box className={classes.dateWrapper} display="flex" alignItems="center">
                                <Typography>Date Created</Typography>
                                {clientHub.created_utc && (
                                    <Typography>{format(new Date(clientHub.created_utc), "dd/MM/yyyy")}</Typography>
                                )}
                            </Box>
                        ) : (
                            <Skeleton variant="rectangular" animation="wave" width={160} height={20} />
                        )}
                    </Box>
                    <NavButton
                        isBackButton
                        variant="outlined"
                        onClick={handleGoToHubManagement}
                        translationId="hubManagement.client.backToHubManagement"
                    />
                </Box>
            </Header>

            <Body>
                <Box className={classes.body}>
                    <Permissions
                        isFetching={isFetching}
                        isLoading={isLoading}
                        permissionsBundle={permissionsBundle}
                        selectedPermission={selectedPermission}
                        onSelectPermission={handleSelectPermission}
                        introText={intl.formatMessage({ id: "hubManagement.createNew.permissionsDescription" })}
                    />
                    <Typography variant="h6" className={classes.sectionTitle}>
                        Users
                    </Typography>
                    <Box className={classes.toolbox}>
                        <Box className={classes.filtersWrapper}>
                            <Box sx={{ maxWidth: 400, mr: 1 }}>
                                <TextField
                                    size="small"
                                    id="name-filter"
                                    name="name-filter"
                                    autoComplete="off"
                                    value={filterUserInput}
                                    className={classes.filterByNameInput}
                                    onChange={handleChangeFilters}
                                    placeholder={intl.formatMessage({
                                        id: "filters.placeholder.user",
                                    })}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <CustomIcon icon={icons.searchInverted} />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Box>
                            <Box sx={{ minWidth: 210, maxWidth: 230 }}>
                                <FormLabel className={classes.label}>
                                    {intl.formatMessage({ id: "filters.labels.role" })}
                                </FormLabel>
                                <Select
                                    multiple
                                    id="roles-filter"
                                    name="roles-filter"
                                    labelId="filter-by-roless"
                                    value={filters.filter_functions}
                                    className={classes.selectField}
                                    onChange={(event) => handleChangeFilters(event)}
                                    IconComponent={(props) => (
                                        <CustomIcon icon={icons.chevronDownLight} {...props} sx={{ fontSize: 12 }} />
                                    )}
                                    MenuProps={{
                                        PaperProps: {
                                            className: classes.selectFilterPaper,
                                        },
                                    }}
                                    renderValue={(selected) => {
                                        const numSelected = selected.length - 1;
                                        const lastSelected = userRoles.find((role) => role.id === selected.at(-1));
                                        return (
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                                <Typography
                                                    className={cn({
                                                        [classes.filterPlaceholderLabel]:
                                                            lastSelected?.id === "placeholder",
                                                    })}
                                                >
                                                    {lastSelected?.label}
                                                </Typography>
                                                {numSelected > 0 && <Chip size="small" label={`+${numSelected}`} />}
                                            </Box>
                                        );
                                    }}
                                >
                                    {userRoles.map((role) => {
                                        return (
                                            <MenuItem key={role.id} value={role.id} className={classes.selectMenuItem}>
                                                <Box display="flex" alignItems="center">
                                                    <Checkbox checked={filters.filter_functions.includes(role.id)} />
                                                    <ListItemText primary={role.label} />
                                                </Box>
                                            </MenuItem>
                                        );
                                    })}
                                    <li className={classes.selectMenuClear}>
                                        <ClearFilterButton
                                            isDisabled={filters.filter_functions.includes("placeholder")}
                                            onClick={() => handleClearFilters("filter_functions")}
                                        />
                                    </li>
                                </Select>
                            </Box>
                        </Box>
                        <NavButton
                            showBackArrow={false}
                            disabled={!org_id}
                            onClick={handleAddSelectedUsers}
                            startIcon={<CustomIcon icon={icons.plus} />}
                            translationId="hubManagement.client.button.addUsers"
                        />
                    </Box>
                    <TablePagination
                        currentPage={clientHubUsers.meta.page_no}
                        totalPages={clientHubUsers.meta.pages}
                        pageValue={inputPage}
                        onPageChange={handlePageChange}
                        handleInputChange={handleInputChange}
                        handleOnBlur={handleOnBlur}
                        handleOnKeyDown={handleOnKeyDown}
                        inputRef={paginationTextInputRef}
                    />
                    {!isFetching && Boolean(clientHub?.users_count) && !Boolean(clientHub?.users_count_org_admin) && (
                        <Collapse
                            orientation="vertical"
                            in={!adminsCount}
                            easing={{ enter: "ease-out", exit: "ease-in" }}
                        >
                            <Alert variant="filled" severity="warning" icon={false} className={classes.errorAlert}>
                                <Box display="flex" justifyContent="center">
                                    <CustomIcon icon={icons.errorTriangle} className={classes.errorIcon} />
                                    <Typography variant="subtitle1" className={classes.errorMessage}>
                                        {intl.formatMessage({ id: "hubManagement.client.error.assignMoreAdmins" })}
                                    </Typography>
                                </Box>
                            </Alert>
                        </Collapse>
                    )}

                    <TableContainer className={classes.tableWrapper}>
                        <Table>
                            <TableHead className={classes.tableHeader}>
                                <TableRow>
                                    {tableHeaderConfig
                                        .filter((cell) => !cell.isHidden)
                                        .map((cell) => {
                                            return (
                                                <TableCell key={cell.id} sx={cell.sx}>
                                                    {!isEmpty(cell.sort) ? (
                                                        <Box className={classes.cellWithSortButton}>
                                                            <SortMenu
                                                                columnId={cell.id}
                                                                currentSortBy={currentSort}
                                                                sortConfig={cell.sort}
                                                                icon={
                                                                    <CustomIcon
                                                                        className={classes.sortButton}
                                                                        icon={icons.sortArrows}
                                                                    />
                                                                }
                                                                {...(!isEmpty(cell.sort)
                                                                    ? {
                                                                          onSort: (order, columnId) =>
                                                                              handleSort(order, columnId),
                                                                      }
                                                                    : {})}
                                                            />
                                                            <Typography>
                                                                {cell.name}{" "}
                                                                {cell.id === "name" &&
                                                                    clientHubUsers.status !== "fetching" &&
                                                                    `(${clientHubUsers.meta.hits})`}
                                                            </Typography>
                                                        </Box>
                                                    ) : (
                                                        <Typography>{cell.name}</Typography>
                                                    )}
                                                </TableCell>
                                            );
                                        })}
                                    <TableCell>&nbsp;</TableCell>
                                    <TableCell>&nbsp;</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody className={classes.tableBody}>
                                <>
                                    {/* Data */}
                                    {clientHubUsers.status !== "fetching" &&
                                        clientHubUsers.data?.map((col) => (
                                            <UsersTableRow
                                                key={col.id}
                                                data={col}
                                                RoleSelectInput={
                                                    <UserRoleSelect
                                                        list={userRoles}
                                                        data={col}
                                                        onRoleSelection={handleUpdateUserRole}
                                                        adminsCount={adminsCount}
                                                    />
                                                }
                                                RemoveUserButton={
                                                    <IconButton
                                                        size="small"
                                                        className={classes.removeUserButton}
                                                        disabled={loggedInUserData.userId === col.id}
                                                        onClick={(event) => handleOpenConfirmationModal(event, col)}
                                                    >
                                                        <Tooltip
                                                            arrow
                                                            placement="bottom-start"
                                                            title={intl.formatMessage({
                                                                id: "hubManagement.client.message.removeUserTooltip",
                                                            })}
                                                        >
                                                            <DeleteOutlinedIcon />
                                                        </Tooltip>
                                                    </IconButton>
                                                }
                                            />
                                        ))}

                                    {/* Fetching */}
                                    {(isFetching && !clientHubUsers.data?.length) ||
                                        (clientHubUsers.status === "fetching" && (
                                            <TableRow>
                                                <TableCell colSpan={5}>
                                                    <Skeleton animation="wave" sx={{ fontSize: 30 }} />
                                                </TableCell>
                                            </TableRow>
                                        ))}

                                    {/* No match */}
                                    {!isFetching &&
                                        clientHubUsers.status !== "fetching" &&
                                        isFiltering &&
                                        !clientHubUsers.data?.length && (
                                            <TableRow>
                                                <TableCell colSpan={5}>
                                                    <Box className={classes.noDataWrapper}>
                                                        <Typography>
                                                            <FormattedMessage
                                                                id="hubManagement.client.usersTable.noMatchesMessage1"
                                                                values={{ string: filters.filter_name }}
                                                            />
                                                        </Typography>
                                                        <Typography>
                                                            <FormattedMessage id="hubManagement.client.usersTable.noMatchesMessage2" />
                                                        </Typography>
                                                        <Box display="flex">
                                                            <NavButton
                                                                showBackArrow={false}
                                                                variant="outlined"
                                                                onClick={() => handleClearFilters("all")}
                                                                startIcon={
                                                                    <CustomIcon
                                                                        icon={icons.close}
                                                                        sx={{ fontSize: 14 }}
                                                                    />
                                                                }
                                                                translationId="hubManagement.client.button.clearSearch"
                                                                sx={{ marginRight: 2 }}
                                                            />
                                                        </Box>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        )}

                                    {/* No data */}
                                    {!isFetching &&
                                        clientHubUsers.status !== "fetching" &&
                                        !isFiltering &&
                                        !clientHubUsers.data?.length && (
                                            <TableRow>
                                                <TableCell colSpan={5}>
                                                    <Box className={classes.noDataWrapper}>
                                                        <Typography>
                                                            <FormattedMessage id="hubManagement.client.usersTable.noDataMessage" />
                                                        </Typography>
                                                        <NavButton
                                                            showBackArrow={false}
                                                            onClick={handleAddSelectedUsers}
                                                            startIcon={<CustomIcon icon={icons.plus} />}
                                                            translationId="hubManagement.client.button.addSelectedUsers"
                                                        />
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                </>
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            </Body>

            <Modal open={Boolean(selectedUser)}>
                <CardMessage
                    hideHeaderBg
                    sx={{ maxWidth: "612px !important" }}
                    title={
                        <Box display="flex" alignItems="center">
                            <CustomIcon
                                icon={icons.circledExclamationSign}
                                sx={{ fontSize: 18, color: "primary.dark", mr: 0.8 }}
                            />
                            <Typography sx={{ fontSize: 20, fontWeight: 600, color: "neutrals.dark" }}>
                                <FormattedMessage id="hubManagement.client.removeUserModal.title" />
                            </Typography>
                        </Box>
                    }
                    content={
                        <Typography variant="subtitle1">
                            {intl.formatMessage(
                                { id: "hubManagement.client.removeUserModal.content" },
                                { userName: selectedUser?.name, clientHub: clientHub.name }
                            )}
                        </Typography>
                    }
                    actions={[
                        {
                            label: <FormattedMessage id="hubManagement.client.removeUserModal.cancelLabel" />,
                            buttonProps: { variant: "outlined" },
                            onClickAction: () => handleCloseConfirmationModal(),
                        },
                        {
                            label: isLoading ? (
                                <></>
                            ) : (
                                <FormattedMessage id="hubManagement.client.removeUserModal.confirmLabel" />
                            ),
                            endIcon: isLoading ? (
                                <CircularProgress size={16} sx={{ color: "common.white" }} />
                            ) : (
                                <CustomIcon icon={icons.chevronRightThin} sx={{ fontSize: 16, color: "white" }} />
                            ),
                            buttonProps: { disabled: isLoading },
                            onClickAction: () => handleRemoveUser(selectedUser),
                        },
                    ]}
                />
            </Modal>
        </>
    );
};

export default ClientHub;
